import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface Language {
  code: string;
  name: string;
  flagImage: string;
  option: string;
  locale: string;
  localizedNames: {
    [key: string]: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private languages: Language[] = [
    { 
      code: 'en', 
      name: 'English', 
      option: 'English', 
      flagImage: 'assets/images/en-US.png', 
      locale: 'en-US',
      localizedNames: {
        'en': 'English',
        'id': 'Bahasa Inggris'
      }
    },
    { 
      code: 'id', 
      name: 'Bahasa Indonesia', 
      option: 'Bahasa', 
      flagImage: 'assets/images/id-ID.png', 
      locale: 'id',
      localizedNames: {
        'en': 'Bahasa',
        'id': 'Bahasa Indonesia'
      }
    },
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(this.languages[0]);
  currentLanguage$: Observable<Language> = this.currentLanguageSubject.asObservable();

  constructor(private translate: TranslateService, private http: HttpClient) {
    // Add supported languages
    this.translate.addLangs(['en', 'id']);
    
    // Set default language
    this.translate.setDefaultLang('en');
    
    // Load saved language preference from localStorage if available
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      const language = this.languages.find(lang => lang.code === savedLanguage);
      if (language) {
        this.currentLanguageSubject.next(language);
        this.loadSalesforceTranslations(language.code);
      }
    } else {
      // Use browser language or default to English
      const browserLang = this.translate.getBrowserLang();
      const lang = browserLang && this.languages.some(l => l.code === browserLang) 
        ? browserLang 
        : 'en';
      
      const language = this.languages.find(l => l.code === lang);
      if (language) {
        this.currentLanguageSubject.next(language);
        this.loadSalesforceTranslations(lang);
      }
    }
    
    // Log current language
    console.log('Current language:', this.translate.currentLang);
  }

  getAllLanguages(): Language[] {
    return this.languages;
  }

  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.getValue();
  }

  getCurrentLanguageName(): string {
    return this.getCurrentLanguage().name;
  }

  getLocalizedLanguageName(language: Language): string {
    const currentLang = this.translate.currentLang || 'en';
    return language.localizedNames[currentLang] || language.name;
  }

  setLanguage(languageCode: string): void {
    const language = this.languages.find(lang => lang.code === languageCode);
    if (language) {
      this.currentLanguageSubject.next(language);
      localStorage.setItem('preferredLanguage', languageCode);
      
      // Load both main and salesforce translations
      this.loadSalesforceTranslations(languageCode);
      console.log('Language changed to:', languageCode);
    }
  }

  private loadSalesforceTranslations(languageCode: string): void {
    // Load both main and salesforce-specific translations
    forkJoin({
      main: this.http.get(`assets/i18n/${languageCode}.json`),
      salesforce: this.http.get(`assets/i18n/salesforce.${languageCode}.json`)
    }).subscribe({
      next: (translations) => {
        // Merge the translations
        const mergedTranslations = { ...translations.main, ...translations.salesforce };
        // Set the merged translations
        this.translate.setTranslation(languageCode, mergedTranslations, true);
        // Use the language
        this.translate.use(languageCode);
      },
      error: (error) => {
        console.warn(`Failed to load salesforce translations for ${languageCode}, falling back to main translations`, error);
        // Fallback to main translations only
        this.translate.use(languageCode);
      }
    });
  }

  // Method to initialize salesforce translations on service startup
  initializeSalesforceTranslations(): void {
    const currentLang = this.translate.currentLang || 'en';
    this.loadSalesforceTranslations(currentLang);
  }
} 